import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useFormattingContext } from '../../FormattingProvider/FormattingContext'
import { useHideIfNoDataWhen } from '../../HideIfNoData'
import HierarchicalOpalGrid, {
  useGridExpandState,
  useGridPageState,
  useGridSortState
} from './HierarchicalOpalGrid'
import { useDDPReportContext } from './DDPReportContext'

const mapAccessorKey = column => column.accessorKey ? column.accessorKey : column.accessor
const mapSize = column => column.size ? column.size : column.width
const mapMaxSize = column => column.maxSize ? column.maxSize : column.maxWidth
const mapMinSize = column => column.minSize ? column.minSize : column.minWidth
const mapHeader = column => column.header ? column.header : column.Header

export const useColumns = (columns, totals) => {
  const { formatter } = useFormattingContext()

  return useMemo(() => {
    const includeFooter = totals?.length > 0
    const templatedColumns = columns.map((column) => {
      const result = {
        ...column,
        header: mapHeader(column),
        accessorKey: mapAccessorKey(column),
        size: mapSize(column),
        maxSize: mapMaxSize(column),
        minSize: mapMinSize(column)
      }
      if (!result?.cell) {
        result.cell = (props) => formatter(props.getValue(), column.format)
      }
      if (includeFooter && !result?.footer) {
        const total = totals.at(0)
        result.footer = () => formatter(total[mapAccessorKey(column)], column.format)
      }

      return result
    })

    return {
      columns: templatedColumns,
      includeFooter
    }
  }, [formatter, columns, totals])
}

function DDPReportGrid ({
  columnConfig,
  defaultSort,
  defaultPageSize,
  pageSizeOptions,
  className,
  style,
  componentClasses,
  componentStyles
}) {
  const { data, isLoading, isFetching, globalFilter, setGlobalFilter } = useDDPReportContext()
  const { pageState, setPageState } = useGridPageState(defaultPageSize || 250)
  const { sortState, setSortState } = useGridSortState(defaultSort)
  const { expandState, setExpandState } = useGridExpandState(null)
  const { columns } = useColumns(columnConfig, [])

  // Used with <HideIfNoData />
  useHideIfNoDataWhen(() => !isLoading && !data?.data?.length, [data, isLoading])

  return (
    <HierarchicalOpalGrid
      className={className}
      style={style}
      componentClasses={componentClasses}
      componentStyles={componentStyles}
      data={data?.data || []}
      columns={columns}
      isFetching={isFetching || isLoading}
      pageState={pageState}
      onPageChange={setPageState}
      expandState={expandState}
      onExpandStateChange={setExpandState}
      sortState={sortState}
      onSortChange={setSortState}
      pageSizeOptions={pageSizeOptions}
      enableFiltering
      globalFilter={globalFilter}
      onGlobalFilterChange={setGlobalFilter}
    />
  )
}

DDPReportGrid.propTypes = {
  /** Column configuration (v2) */
  columnConfig: PropTypes.array,

  /** Default sort state of the grid */
  defaultSort: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    desc: PropTypes.bool
  })),

  /** Default page size */
  defaultPageSize: PropTypes.number,

  /** Page size options for selector */
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),

  className: PropTypes.string,
  style: PropTypes.object,
  componentClasses: PropTypes.shape({
    table: PropTypes.string,
    header: PropTypes.string,
    cell: PropTypes.string,
    footer: PropTypes.string
  }),
  componentStyles: PropTypes.shape({
    table: PropTypes.object,
    header: PropTypes.object,
    cell: PropTypes.object,
    footer: PropTypes.object
  })
}

DDPReportGrid.defaultProps = {
  defaultSort: [],
  defaultPageSize: 250,
  pageSizeOptions: [50, 100, 250, 500, 1000]
}

export default DDPReportGrid
